import { Box, Button, useToast } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { ApiMessageStack, Message, TableV2 } from "components";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { IntegrationEntitySyncStatusEnum } from "enums/integration-entity-sync-status.enum";
import { AlertStatusEnum } from "enums/alert-status.enum";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { keyBy } from "lodash";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { syncAltoLeadSourceTableColumnDef } from "./syncAltoLeadSourceTableColumnDef";
import { TAltoSyncLeadSourceExtendedData } from "types/alto.type";
import { IntegrationConfiguratorLayout } from "../../IntegrationConfiguratorLayout";
import { SyncStatusFilter } from "../../SyncStatusFilter";

const DEFAULT_PAGE_SIZE = 10;

interface IProps {
  accountStore: UserAccountStore;
}

export const AltoIntegrationsLeadSourcesConfigurator: FC<IProps> = observer(
  ({ accountStore }) => {
    const toast = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newRelations, setNewRelations] = useState<
      Record<number, TAltoSyncLeadSourceExtendedData>
    >({});

    const accountIntegrationsStore = accountStore.accountIntegrationsStore;
    const accountIntegrationsAltoStore =
      accountIntegrationsStore.accountIntegrationAltoStore;
    const accountIntegrationAltoLeadSourcesStore =
      accountIntegrationsAltoStore.accountIntegrationAltoLeadSourcesStore;

    const {
      altoSyncLeadSourceData,
      filteredAltoSyncLeadSourceData,
      getSyncStatusFilter,
      setSyncStatusFilter,
    } = accountIntegrationAltoLeadSourcesStore;

    const updateSyncPair = useCallback(
      (syncPair: TAltoSyncLeadSourceExtendedData) => {
        setNewRelations({
          ...newRelations,
          [syncPair.leadSourceId]: syncPair,
        });
      },
      [setNewRelations, newRelations]
    );

    useEffect(() => {
      const suggestedArray = altoSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus === IntegrationEntitySyncStatusEnum.SUGGESTED_SYNC
      );
      const suggestedMap = keyBy<TAltoSyncLeadSourceExtendedData>(
        suggestedArray,
        syncData => syncData.leadSourceId
      );
      setNewRelations({
        ...suggestedMap,
      });
    }, [altoSyncLeadSourceData]);

    const columnsDef = useMemo(() => {
      return syncAltoLeadSourceTableColumnDef(accountStore, updateSyncPair);
    }, [accountStore, updateSyncPair]);

    const dataSource = useMemo(() => {
      return filteredAltoSyncLeadSourceData.map(syncData => {
        if (!!newRelations[syncData.leadSourceId])
          return newRelations[syncData.leadSourceId];

        return syncData;
      });
    }, [filteredAltoSyncLeadSourceData, newRelations]);

    const submitSyncPairs = useCallback(async () => {
      try {
        setIsSubmitting(true);
        await accountIntegrationAltoLeadSourcesStore.updateAccountAltoSyncedLeadSources(
          Object.values(newRelations)
        );
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack
              messageStack={
                "Alto integration lead sources configuration updated."
              }
            />
          ),
        });
      } catch (error) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={error.message} />,
        });
      } finally {
        setIsSubmitting(false);
      }
    }, [newRelations, accountIntegrationAltoLeadSourcesStore, toast]);

    const banner = useMemo(() => {
      const notSyncedArray = altoSyncLeadSourceData.filter(
        syncData =>
          syncData.syncStatus !== IntegrationEntitySyncStatusEnum.SYNCED
      );

      if (notSyncedArray.length > 0) {
        return (
          <Message status={AlertStatusEnum.WARNING}>
            <Box>
              {`${notSyncedArray.length} of your lead sources are not syncing to LeadPro`}
            </Box>
          </Message>
        );
      }

      return null;
    }, [altoSyncLeadSourceData]);

    const handleSyncFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSyncStatusFilter(e.target.checked);
      },
      [setSyncStatusFilter]
    );

    return (
      <IntegrationConfiguratorLayout>
        <Box fontSize={"xl"}>Lead source mappings</Box>
        {banner}
        <TableV2<TAltoSyncLeadSourceExtendedData>
          globalFilterInputPlaceholder={"Search sources by name"}
          dataSource={dataSource}
          additionalActions={
            <SyncStatusFilter
              isChecked={getSyncStatusFilter}
              onChange={handleSyncFilterChange}
            />
          }
          columns={columnsDef}
          pageSize={DEFAULT_PAGE_SIZE}
        />
        <Box width={"100%"} textAlign={"right"}>
          <Button
            colorScheme={"blue"}
            onClick={submitSyncPairs}
            isLoading={isSubmitting}
          >
            Update
          </Button>
        </Box>
      </IntegrationConfiguratorLayout>
    );
  }
);
